import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import MenuItem from '@mui/material/MenuItem';
import { GBENSContext } from '../GBENSContainer';

import Row from './_Row';

const SimpleSelect = ({
  id,
  screen,
  modal,
  placeholder,
  options,
  consignment,
  item,
  name,
  toolTip,
  localErrors,
  rowProps,
  onBeforeChange,
  disabled,
  isAutocomplete,
  allowUserInputAsValue = false,
}) => {
  const {
    currentConsignmentErrors,
    currentConsignment,
    data,
    updateData,
    errors,
    errorsLocal,
    updateCurrentConsignment,
    currentLine,
    updateCurrentLine,
  } = useContext(GBENSContext);

  //Calculate Error
  const modalGlobal = modal?.startsWith?.('_') ? modal.slice(1, modal.length) : modal;
  const _error = localErrors
    ? (errorsLocal?.[modalGlobal]?.[id] ?? null)
    : item
      ? (currentConsignmentErrors?.lines?.find?.((l) => l.id === currentLine.id)?.[id] ?? null)
      : consignment
        ? modal
          ? (currentConsignmentErrors?.[modalGlobal]?.[id] ?? null)
          : (currentConsignmentErrors?.[id] ?? null)
        : modal
          ? (errors?.[screen]?.[modalGlobal]?.[id] ?? null)
          : (errors?.[screen]?.[id] ?? null);

  //Calculate Value
  const value = item
    ? (currentLine?.[id] ?? '')
    : consignment
      ? modal
        ? (currentConsignment?.[modal]?.[id] ?? '')
        : (currentConsignment?.[id] ?? '')
      : modal
        ? (data?.[screen]?.[modal]?.[id] ?? '')
        : (data?.[screen]?.[id] ?? '');

  const onChange = (e) => {
    if (item) {
      if (typeof e?.target?.value !== 'undefined') {
        updateCurrentLine(e, id);
      }
    } else if (consignment) {
      updateCurrentConsignment(e, id, modal);
    } else {
      updateData(e, screen, id, modal);
    }
  };

  // If using autocomplete, we need to create a 'fake' event object for the onChange
  const onAutocompleteChange = (e, selectedOption) => {
    const event = {
      target: {
        value: selectedOption?.value ?? '',
      },
    };
    onChange(event);
  };

  // If using autocomplete, then the value for the component is different from the Select
  const autocompleteValue = useMemo(() => {
    // if using free text value then we should display this in the auto-complete
    const backupValue = allowUserInputAsValue ? value : null;
    const selectedOption = options?.find((opt) => opt.key == value);
    return selectedOption ? { label: selectedOption.value, value: selectedOption.key } : backupValue;
  }, [value, options]);

  return (
    <Row
      name={name}
      warning={_error}
      toolTip={toolTip}
      rowProps={rowProps}
      component={
        <FormControl
          fullWidth
          sx={{
            '& .MuiInput-underline:before': {
              borderBottom: 'none !important', // Ensures underline is removed before focus
            },
            '& .MuiInput-underline:hover:before': {
              borderBottom: 'none !important', // Ensures underline is removed on hover
            },
            '& .MuiInput-underline:after': {
              borderBottom: 'none !important', // Ensures underline is removed after focus
            },
          }}
        >
          {isAutocomplete ? (
            <Autocomplete
              variant="standard"
              value={autocompleteValue}
              onChange={onAutocompleteChange}
              onInputChange={allowUserInputAsValue ? onChange : null}
              options={options?.map((opt) => {
                return { label: opt.value, value: opt.key };
              })}
              getOptionKey={(option) => option.value}
              renderInput={(params) => <TextField {...params} placeholder={placeholder || ' - Select - '} />}
              disabled={disabled}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: 'flip',
                      options: {
                        rootBoundary: 'document',
                      },
                    },
                  ],
                },
              }}
              freeSolo={allowUserInputAsValue}
            />
          ) : (
            <Select
              id={id}
              value={(value ?? null) ? value : -1}
              onChange={(e) => {
                if (onBeforeChange) onBeforeChange(e.target.value);
                onChange(e);
              }}
              sx={{
                color: ((value ?? null) ? value : -1) === -1 ? 'grey' : 'inherit',
              }}
              MenuProps={{ PaperProps: { sx: { maxHeight: 265 } } }}
              disabled={disabled}
            >
              <MenuItem value={-1} style={{ color: 'grey' }}>
                {placeholder || ' - Select - '}
              </MenuItem>
              {options
                ? options.map((opt) => (
                    <MenuItem
                      component={(props) => (
                        <li title={props.children[0]} {...props}>
                          {props.children[0].length > 50 ? props.children[0].slice(0, 50) + '...' : props.children[0]}
                        </li>
                      )}
                      key={opt.key}
                      value={opt.key}
                    >
                      {opt.value}
                    </MenuItem>
                  ))
                : null}
            </Select>
          )}
        </FormControl>
      }
    />
  );
};
SimpleSelect.propTypes = {
  placeholder: PropTypes.string,
  id: PropTypes.string,
  screen: PropTypes.string,
  options: PropTypes.array,
  modal: PropTypes.string,
  consignment: PropTypes.bool,
  item: PropTypes.bool,
  name: PropTypes.string,
  toolTip: PropTypes.string,
  rowProps: PropTypes.object,
  localErrors: PropTypes.bool,
  onBeforeChange: PropTypes.func,
  disabled: PropTypes.bool,
  isAutocomplete: PropTypes.bool,
  allowUserInputAsValue: PropTypes.bool,
};

export default SimpleSelect;
